import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Card = styled(motion.div)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const CardImage = styled.div`
  height: 220px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.5), transparent);
    z-index: 1;
  }
`;

const PatternOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.pattern});
  background-size: cover;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
`;

const CardBadge = styled.span`
  position: absolute;
  top: 20px;
  right: 20px;
  background: ${props => props.badgeColor || '#0066cc'};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  z-index: 2;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: ${props => props.color || '#333'};
`;

const CardDescription = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  flex: 1;
`;

const CardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const CardTag = styled.span`
  background: ${props => props.tagBackground || '#f0f4f8'};
  color: ${props => props.tagColor || '#0066cc'};
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardButton = styled(Link)`
  background: ${props => props.buttonColor || '#0066cc'};
  color: white;
  text-decoration: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: background 0.3s;
  display: inline-flex;
  align-items: center;
  
  &:hover {
    background: ${props => props.buttonHoverColor || '#0055aa'};
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const CardStats = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.9rem;
  
  svg {
    margin-right: 0.4rem;
    color: ${props => props.iconColor || '#0066cc'};
  }
`;

const ProjectCard = ({ 
  title,
  description,
  image,
  pattern,
  badge,
  badgeColor,
  tags = [],
  tagBackground,
  tagColor,
  linkTo,
  buttonText = 'Learn More',
  buttonColor,
  buttonHoverColor,
  iconColor,
  animationDelay = 0,
  titleColor
}) => {
  return (
    <Card
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: animationDelay }}
      whileHover={{ 
        y: -10,
        boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15)'
      }}
    >
      <CardImage image={image}>
        {pattern && <PatternOverlay pattern={pattern} />}
        {badge && <CardBadge badgeColor={badgeColor}>{badge}</CardBadge>}
      </CardImage>
      
      <CardContent>
        <CardTitle color={titleColor}>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        
        {tags.length > 0 && (
          <CardTags>
            {tags.map((tag, index) => (
              <CardTag 
                key={index}
                tagBackground={tagBackground}
                tagColor={tagColor}
              >
                {tag}
              </CardTag>
            ))}
          </CardTags>
        )}
        
        <CardFooter>
          <CardButton 
            to={linkTo}
            buttonColor={buttonColor}
            buttonHoverColor={buttonHoverColor}
          >
            {buttonText}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </CardButton>
        </CardFooter>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;
